import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Button, IconButton } from '@mui/material';
import { Table, Tag } from 'antd';
import { ArrowBack, Edit, Visibility } from '@mui/icons-material';
import { getCampaignStages, getMarketingById, getCampaignById, getCampaignFlow } from '../services/api';
import { toast } from 'react-toastify';
import 'antd/dist/reset.css';

function CampaignStages() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [campaign, setCampaign] = useState(null);
    const [stages, setStages] = useState([]);
    const [futureStageOrders, setFutureStageOrders] = useState({});

    useEffect(() => {
        fetchCampaignDetails();
        fetchStages();
    }, [id]);

    const fetchCampaignDetails = async () => {
        try {
            const res = await getCampaignById(id);
            setCampaign(res.data.data);
        } catch (error) {
            console.error(error);
            toast.error('Failed to fetch campaign details');
        }
    };

    const fetchStages = async () => {
        try {
            const res = await getCampaignFlow(id);
            console.log(res.data.data);
            setStages(res.data.data);
        } catch (error) {
            console.error(error);
            toast.error('Failed to fetch stages');
        }
    };

    const stageColumns = [
        {
            title: 'S.No',
            dataIndex: 'sno',
            key: 'sno',
            width: 80,
            fixed: 'left',
        },
        {
            title: 'Stage Name',
            dataIndex: 'stageName',
            key: 'stageName',
            width: 250,
            fixed: 'left',
        },
        {
            title: 'Stakeholders',
            dataIndex: 'stakeholders',
            key: 'stakeholders',
            width: 200,
        },
        {
            title: 'Ack From',
            dataIndex: 'ackFrom',
            key: 'ackFrom',
            width: 200,
        },
        {
            title: 'Workflow Type',
            dataIndex: 'workflowType',
            key: 'workflowType',
            width: 150,
        },
        {
            title: 'SLA',
            dataIndex: 'sla',
            key: 'sla',
            width: 150,
            render: (value) => value ? `${value} Days` : '-',
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 150,
            render: (_, record) => {
                const fwd = record.fwd;
                const reply = record.reply;
                const status = record.status;

                let label = 'Pending';
                let color = 'default';

                if (fwd == 1 && reply == 1 && status == 1) {
                    label = 'Completed';
                    color = 'success';
                } else if (fwd == 1 && reply == 1 && status == 3) {
                    label = 'Rejected';
                    color = 'error';
                } else if (fwd == 1 && reply == 0 && status == 0) {
                    label = 'In Progress';
                    color = 'processing';
                } else {
                    label = 'Pending';
                    color = 'default';
                }

                return <Tag color={color} style={{ borderRadius: '12px' }}>{label}</Tag>;
            },
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 150,
            render: (_, record) => {
                const fwd = record.fwd;
                const reply = record.reply;
                const status = record.status;

                let isCompleted = fwd == 1 && reply == 1 && status == 1;
                let isRejected = fwd == 1 && reply == 1 && status == 3;
                let isInProgress = fwd == 1 && reply == 0 && status == 0;

                const handleEdit = () => {
                    if (!isInProgress) return;

                    const isStrategy = record.stageName === 'Strategy' || record.sno === 2;
                    const isApproval = record.stageName === 'Strategy Approval' || record.sno === 3;
                    const isPlanning = record.stageName === 'Influencer Planning' || record.sno === 4;
                    const isShortlisting = record.stageName === 'Influencer Shortlisting' || record.stageName === 'Shortlisting' || record.sno === 5;
                    const isBusinessApproval = record.stageName === 'Business Approval' || record.sno === 6;
                    const isFinalCosts = record.stageName === 'Influencer Final Costs' || record.stageName === 'Final Costs' || record.sno === 7;
                    const isEmpanelment = record.stageName === 'Influencer Empanelment' || record.sno === 8;
                    const isBudgetRelease = record.stageName === 'Budget Release' || record.sno === 9;
                    const isPODispatch = record.stageName === 'PO to Influencer' || record.stageName === 'PO Dispatch' || record.sno === 10;
                    const isProductRequirement = record.stageName === 'Product Requirement' || record.stageName === 'Product requirement' || record.sno === 11;
                    const isContentCoordination = record.stageName === 'Content Coordination' || record.sno === 12;
                    const isLogistics = record.stageName === 'Logistics Team' || record.sno === 13;
                    const isContentLive = record.stageName === 'Content & Video Live' || record.sno === 14;
                    const isAssetCollection = record.stageName === 'Asset Collection' || record.sno === 15;
                    const isBillingCoordination = record.stageName === 'Billing Coordination' || record.stageName === 'Billing Confirmation' || record.sno === 16;

                    let route = `/marketing/edit/${id}/stage/${record._id}`;
                    if (isStrategy) route = `/marketing/edit/${id}/strategy/${record._id}`;
                    if (isApproval) route = `/marketing/edit/${id}/approval/${record._id}`;
                    if (isPlanning) route = `/marketing/edit/${id}/planning/${record._id}`;
                    if (isShortlisting) route = `/marketing/edit/${id}/shortlisting/${record._id}`;
                    if (isBusinessApproval) route = `/marketing/edit/${id}/business-approval/${record._id}`;
                    if (isFinalCosts) route = `/marketing/edit/${id}/final-costs/${record._id}`;
                    if (isEmpanelment) route = `/marketing/edit/${id}/empanelment/${record._id}`;
                    if (isBudgetRelease) route = `/marketing/edit/${id}/budget-release/${record._id}`;
                    if (isPODispatch) route = `/marketing/edit/${id}/po-dispatch/${record._id}`;
                    if (isProductRequirement) route = `/marketing/edit/${id}/product-requirement/${record._id}`;
                    if (isContentCoordination) route = `/marketing/edit/${id}/content-coordination/${record._id}`;
                    if (isLogistics) route = `/marketing/edit/${id}/logistics/${record._id}`;
                    if (isContentLive) route = `/marketing/edit/${id}/content-live/${record._id}`;
                    if (isAssetCollection) route = `/marketing/edit/${id}/asset-collection/${record._id}`;
                    if (isBillingCoordination) route = `/marketing/edit/${id}/billing-confirmation/${record._id}`;

                    navigate(route, {
                        state: {
                            stageName: record.stageName,
                            campaignTitle: campaign?.campaignName
                        }
                    });
                };

                const handleView = () => {
                    const isStrategy = record.stageName === 'Strategy' || record.sno === 2;
                    const isApproval = record.stageName === 'Strategy Approval' || record.sno === 3;
                    const isPlanning = record.stageName === 'Influencer Planning' || record.sno === 4;
                    const isShortlisting = record.stageName === 'Influencer Shortlisting' || record.stageName === 'Shortlisting' || record.sno === 5;
                    const isBusinessApproval = record.stageName === 'Business Approval' || record.sno === 6;
                    const isFinalCosts = record.stageName === 'Influencer Final Costs' || record.stageName === 'Final Costs' || record.sno === 7;
                    const isEmpanelment = record.stageName === 'Influencer Empanelment' || record.sno === 8;
                    const isBudgetRelease = record.stageName === 'Budget Release' || record.sno === 9;
                    const isPODispatch = record.stageName === 'PO to Influencer' || record.stageName === 'PO Dispatch' || record.sno === 10;
                    const isProductRequirement = record.stageName === 'Product Requirement' || record.stageName === 'Product requirement' || record.sno === 11;
                    const isContentCoordination = record.stageName === 'Content Coordination' || record.sno === 12;
                    const isLogistics = record.stageName === 'Logistics Team' || record.sno === 13;
                    const isContentLive = record.stageName === 'Content & Video Live' || record.sno === 14;
                    const isAssetCollection = record.stageName === 'Asset Collection' || record.sno === 15;
                    const isBillingCoordination = record.stageName === 'Billing Coordination' || record.stageName === 'Billing Confirmation' || record.sno === 16;

                    let route = `/marketing/edit/${id}/stage/${record._id}`;
                    if (isStrategy) route = `/marketing/edit/${id}/strategy/${record._id}`;
                    if (isApproval) route = `/marketing/edit/${id}/approval/${record._id}`;
                    if (isPlanning) route = `/marketing/edit/${id}/planning/${record._id}`;
                    if (isShortlisting) route = `/marketing/edit/${id}/shortlisting/${record._id}`;
                    if (isBusinessApproval) route = `/marketing/edit/${id}/business-approval/${record._id}`;
                    if (isFinalCosts) route = `/marketing/edit/${id}/final-costs/${record._id}`;
                    if (isEmpanelment) route = `/marketing/edit/${id}/empanelment/${record._id}`;
                    if (isBudgetRelease) route = `/marketing/edit/${id}/budget-release/${record._id}`;
                    if (isPODispatch) route = `/marketing/edit/${id}/po-dispatch/${record._id}`;
                    if (isProductRequirement) route = `/marketing/edit/${id}/product-requirement/${record._id}`;
                    if (isContentCoordination) route = `/marketing/edit/${id}/content-coordination/${record._id}`;
                    if (isLogistics) route = `/marketing/edit/${id}/logistics/${record._id}`;
                    if (isContentLive) route = `/marketing/edit/${id}/content-live/${record._id}`;
                    if (isAssetCollection) route = `/marketing/edit/${id}/asset-collection/${record._id}`;
                    if (isBillingCoordination) route = `/marketing/edit/${id}/billing-confirmation/${record._id}`;

                    navigate(route, {
                        state: {
                            stageName: record.stageName,
                            campaignTitle: campaign?.campaignName,
                            viewMode: true
                        }
                    });
                };

                return (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {!isCompleted && !isRejected && (
                            <IconButton
                                size="small"
                                disabled={!isInProgress}
                                onClick={handleEdit}
                                sx={{
                                    color: isInProgress ? '#5386e4' : '#ccc',
                                    cursor: isInProgress ? 'pointer' : 'not-allowed',
                                }}
                                title="Edit"
                            >
                                <Edit />
                            </IconButton>
                        )}
                        {(isCompleted || isRejected) && (
                            <IconButton
                                size="small"
                                onClick={handleView}
                                sx={{ color: '#5386e4' }}
                                title="View"
                            >
                                <Visibility />
                            </IconButton>
                        )}
                    </Box>
                );
            },
        },
    ];

    const rows = stages.map((stage, index) => {
        return ({
            ...stage,
            key: stage.campaignStageId._id,
            sno: index + 1,
            stageName: stage.campaignStageId.stageName,
            workflowType: stage.campaignStageId.workflowType,
            ackFrom: stage.campaignStageId.ackFrom,
            sla: stage.campaignStageId.sla,
            stakeholders: stage.campaignStageId.stakeholders,
            allData: stage,
        })
    })

    return (
        <Box sx={{ p: 3 }}>
            <Button startIcon={<ArrowBack />} onClick={() => navigate('/marketing')} sx={{ mb: 2 }}>
                Back to Campaigns
            </Button>

            <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
                Edit Campaign & Workflow
            </Typography>

            <Box sx={{ bgcolor: 'white', p: 3, borderRadius: 2, boxShadow: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">Workflow Stages</Typography>
                    <Typography variant="subtitle1" fontWeight="bold" color="text.secondary">
                        Campaign Name: {campaign?.campaignName}
                    </Typography>
                </Box>
                <Table
                    columns={stageColumns}
                    dataSource={rows}
                    scroll={{ x: 1200 }}
                    pagination={false}
                    size="small"
                />
            </Box>
        </Box>
    );
}

export default CampaignStages;